import type { SectionRegistration } from '@vextura/shell';
import { MANIFESTS } from '@vextura/registry';

/**
 * Section registry. Each entry maps a nav section id to a lazy dynamic
 * import of either:
 *   - a `@vextura/service-*` package's default-exported React component
 *     (canonical), OR
 *   - an apps/console-local screen (for shell-adjacent pages like /me).
 *
 * Lazy imports MUST be literal `() => import('@vextura/service-xxx')`
 * strings — Vite's code-splitter needs them at build time. This file is
 * hand-edited; the registry merely DETECTS drift between this list and
 * the manifests on disk (see warning below). When a new service section
 * ships, add ONE line here paired with its `service.yaml`.
 */
const ALL_SECTIONS: ReadonlyArray<SectionRegistration> = [
  { id: 'iam', load: () => import('@vextura/service-iam') },
  { id: 'auth', load: () => import('@vextura/service-auth') },
  { id: 'vault', load: () => import('@vextura/service-vault') },
  { id: 'gate', load: () => import('@vextura/service-gate') },
  { id: 'fn', load: () => import('@vextura/service-fn') },
  { id: 'uwf', load: () => import('@vextura/service-uwf') },
  { id: 'mcp', load: () => import('@vextura/service-mcp') },
  { id: 'pipeline', load: () => import('@vextura/service-pipeline') },
  { id: 'me', load: () => import('./Profile.js') },
  { id: 'storybook', load: () => import('./Storybook.js') },
];

/**
 * Feature-flag-filtered section list. Read at app start (readRuntimeConfig
 * memoizes the window config), so the sidebar reflects the container's
 * env-injected flags. Sections whose flag is false are NOT registered —
 * their lazy chunks never get requested and the sidebar entry never renders.
 *
 * antifraud / incident-mgr / docparse were split into standalone console
 * apps (antifraud-console, incident-mgr-console, doc-parse-console) so
 * this file no longer references them. The registry manifests + codegen
 * entries + shell env flags were dropped in the same pass.
 */
function filterByFlags(all: ReadonlyArray<SectionRegistration>): ReadonlyArray<SectionRegistration> {
  // UWF section base is always available. Live-stream is opt-in inside
  // service-uwf via readRuntimeConfig().enableUwfStreaming — the section
  // itself stays visible.
  return all;
}

export const SECTIONS: ReadonlyArray<SectionRegistration> = filterByFlags(ALL_SECTIONS);

// Dev-time drift detector: warn if any manifest lacks a section entry
// (forgot to wire the lazy import) or vice versa (stale entry pointing
// at a removed manifest). Tree-shaken out of prod builds.
if (import.meta.env['DEV']) {
  const registered = new Set(SECTIONS.map((s) => s.id));
  for (const id of Object.keys(MANIFESTS)) {
    if (!registered.has(id)) {
      console.warn(
        `[sections] manifest "${id}" has no SectionRegistration. Add one line: ` +
          `{ id: '${id}', load: () => import('@vextura/service-${id}') }`,
      );
    }
  }
}
